import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Video, Home as HomeIcon, PhoneCall, Sparkles, Handshake, MapPin, Car } from "lucide-react";
import { SERVICES, NEEDS, CONTACT_INFO } from "@/lib/data";
import MapZone from "@/components/MapZone";

const PARTNER_LOGO = "https://delamainalapatte21.fr/wp-content/uploads/2022/06/LOGO-ROND.png";

export default function Services() {
  return (
    <div data-testid="services-page">
      {/* Header */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20 md:py-28">
        <p className="text-xs tracking-[0.25em] uppercase text-[#8B7E74] mb-6 fade-up">Services & Tarifs</p>
        <h1 data-testid="services-title" className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#4A403A] leading-[1.05] max-w-4xl fade-up fade-up-delay-1">
          Six manières<br/>
          de <em className="text-[#BA8E82]">cheminer ensemble</em>,<br/>
          personnalisées pour votre famille.
        </h1>
        <p className="mt-8 text-lg text-[#8B7E74] leading-relaxed max-w-2xl fade-up fade-up-delay-2">
          <strong className="text-[#4A403A] font-medium">1<sup>er</sup> appel gratuit</strong> afin d'établir un premier contact, aussi disponible par messages.
          Disponible du lundi au samedi de 8h à 18h, à Dijon et ses agglomérations (maximum 45 min).
        </p>

        <div className="mt-10 flex flex-wrap gap-3 fade-up fade-up-delay-3">
          <span data-testid="badge-visio" className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-[#F4EBE6] text-[#BA8E82] text-xs tracking-wider uppercase">
            <Video size={14} /> Visio disponible pour les prestations marquées * · −20%
          </span>
          <span className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-[#F4EBE6] text-[#BA8E82] text-xs tracking-wider uppercase">
            <HomeIcon size={14} /> Intervention à domicile
          </span>
          <span className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-[#F4EBE6] text-[#BA8E82] text-xs tracking-wider uppercase">
            Remboursement possible sur certaines mutuelles
          </span>
        </div>

        <p className="mt-6 text-sm text-[#8B7E74] max-w-3xl fade-up fade-up-delay-3">
          Remboursement de certaines mutuelles possible sous l'onglet "pratique alternative",
          "bien-être", "coaching", "soutien à la parentalité", etc.
        </p>
      </section>

      {/* Où en êtes-vous ? — rappel */}
      <section className="bg-[#F4EBE6]/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-24">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div className="max-w-xl">
              <p className="text-xs tracking-[0.25em] uppercase text-[#8B7E74] mb-4">Où en êtes-vous&nbsp;?</p>
              <h2 className="font-serif text-3xl md:text-4xl text-[#4A403A] leading-tight">
                Vous vous reconnaissez ici&nbsp;?<br/>
                <em className="text-[#BA8E82]">Choisissez la prestation adaptée</em>.
              </h2>
            </div>
            <Link
              to="/#ou-en-etes-vous"
              data-testid="services-back-needs"
              className="text-sm text-[#4A403A] link-underline"
            >
              Voir les 6 situations en détail →
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {NEEDS.map((n) => (
              <div
                key={n.key}
                data-testid={`services-need-mini-${n.key}`}
                className="bg-white rounded-2xl p-5 border border-[#E5DCD6] text-center"
              >
                <span className="text-2xl mb-2 block" aria-hidden="true">{n.emoji}</span>
                <p className="text-sm font-medium text-[#4A403A] leading-tight">{n.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bento services */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-6 auto-rows-fr">
          {SERVICES.map((s, i) => {
            const spans = [
              "lg:col-span-4",
              "lg:col-span-2",
              "lg:col-span-2",
              "lg:col-span-4",
              "lg:col-span-3",
              "lg:col-span-3",
            ];
            return (
              <article
                key={s.slug}
                data-testid={`service-card-${s.slug}`}
                className={`bg-white rounded-3xl p-6 sm:p-8 md:p-10 border border-[#E5DCD6] shadow-softer transition-all duration-300 hover:-translate-y-1 hover:shadow-soft flex flex-col ${spans[i]}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-6 mb-6">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <span className="text-xs tracking-[0.18em] uppercase text-[#BA8E82] bg-[#F4EBE6] rounded-full px-3 py-1">
                        {s.badge}
                      </span>
                      {s.visio && (
                        <span data-testid={`service-visio-${s.slug}`} className="inline-flex items-center gap-1 text-xs tracking-wider uppercase text-[#BA8E82] bg-[#FAF7F2] border border-[#E5DCD6] rounded-full px-3 py-1">
                          <Video size={12} /> Visio −20%
                        </span>
                      )}
                    </div>
                    <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-[#4A403A] leading-tight break-words">
                      {s.title}{s.visio && <span className="text-[#BA8E82] ml-1">*</span>}
                    </h2>
                    {s.subtitle && (
                      <p className="text-sm text-[#8B7E74] mt-2 italic">{s.subtitle}</p>
                    )}
                  </div>
                  <div className="text-left sm:text-right sm:shrink-0">
                    <p className="font-serif text-3xl md:text-4xl text-[#4A403A] leading-none">{s.price}</p>
                    <p className="text-xs text-[#8B7E74] mt-2 sm:max-w-[160px]">{s.duration}</p>
                  </div>
                </div>
                <p className="text-[#8B7E74] leading-relaxed mb-6 break-words">{s.description}</p>

                <ul className="space-y-3 mb-8">
                  {s.points.map((p) => (
                    <li key={p} className="flex gap-3 text-sm text-[#4A403A]">
                      <CheckCircle2 size={16} className="text-[#D2A69A] shrink-0 mt-0.5" strokeWidth={1.6} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/rendez-vous"
                  state={{ service: s.title }}
                  data-testid={`service-book-${s.slug}`}
                  className="mt-auto inline-flex items-center gap-2 rounded-full px-6 py-2.5 bg-[#4A403A] text-[#FAF7F2] text-sm tracking-wide self-start transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#3a322d]"
                >
                  Réserver cette séance <ArrowRight size={14} />
                </Link>
              </article>
            );
          })}
        </div>

        {/* Motto */}
        <div className="mt-16 text-center">
          <p className="inline-flex items-center gap-3 font-serif text-2xl md:text-3xl italic text-[#4A403A] tracking-wide">
            <Sparkles size={18} className="text-[#D2A69A]" />
            À vos côtés et au cœur de votre famille pour plus de sérénité
            <Sparkles size={18} className="text-[#D2A69A]" />
          </p>
        </div>
      </section>

      {/* Partenaires */}
      <section data-testid="partners-section" className="max-w-6xl mx-auto px-6 lg:px-10 py-20 md:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#8B7E74] mb-4">
            <Handshake size={14} /> Partenaires
          </p>
          <h2 className="font-serif text-4xl md:text-5xl text-[#4A403A] leading-tight">
            Nous nous faisons confiance<br/>
            <em className="text-[#BA8E82]">et travaillons ensemble</em>.
          </h2>
        </div>

        <a
          href="https://delamainalapatte21.fr/qui-suis-je/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="partner-delamainalapatte"
          className="group flex flex-col sm:flex-row items-center gap-6 sm:gap-8 bg-white rounded-3xl p-8 border border-[#E5DCD6] shadow-softer max-w-3xl mx-auto transition-all duration-300 hover:-translate-y-1 hover:shadow-soft"
        >
          <span className="w-24 h-24 rounded-full overflow-hidden bg-[#FAF7F2] border border-[#E5DCD6] flex items-center justify-center shrink-0">
            <img src={PARTNER_LOGO} alt="De la main à la patte 21 — éducatrice canine" className="w-full h-full object-contain p-2" />
          </span>
          <div className="text-center sm:text-left">
            <h3 className="font-serif text-2xl text-[#4A403A] mb-2">De la main à la patte 21</h3>
            <p className="text-[#8B7E74] leading-relaxed">
              Éducatrice canine positive et bienveillante en Côte-d'Or : ensemble, nous aidons les familles
              à instaurer une cohabitation sereine entre l'enfant et le chien de la maison.
            </p>
            <span className="mt-3 inline-flex items-center gap-2 text-sm text-[#4A403A] link-underline">
              Découvrir <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </span>
          </div>
        </a>
      </section>

      {/* Info pratique */}
      <section className="bg-[#F4EBE6]/60">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24 md:py-28">
          <div className="grid md:grid-cols-2 gap-10">
            <div>
              <p className="text-xs tracking-[0.25em] uppercase text-[#8B7E74] mb-4">Bon à savoir</p>
              <h2 className="font-serif text-4xl md:text-5xl text-[#4A403A] leading-tight mb-6">
                Une parenthèse pour votre famille.
              </h2>
              <p className="text-[#8B7E74] text-lg leading-relaxed mb-6">
                Toutes les séances se déroulent dans la pièce de vie de votre choix — chez vous,
                là où votre enfant est en confiance. Les séances marquées d'une * sont également
                disponibles en visio, avec une réduction de 20%.
              </p>
              <a
                href={`tel:${CONTACT_INFO.phoneRaw}`}
                data-testid="services-call-cta"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-[#4A403A] text-[#FAF7F2] text-sm tracking-wide transition-all hover:-translate-y-0.5"
              >
                <PhoneCall size={15} /> 1<sup>er</sup> appel gratuit — {CONTACT_INFO.phone}
              </a>
            </div>
            <div className="space-y-5">
              {[
                ["Zone d'intervention", CONTACT_INFO.zone],
                ["Modalité", "À domicile ou en visio (−20%, séances marquées *)"],
                ["Règlement", "Espèces, chèque ou virement"],
                ["Mutuelles", "Remboursement possible — me consulter"],
                ["1er contact", "Appel ou message gratuit"],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-6 border-b border-[#E5DCD6] pb-4">
                  <span className="text-[#8B7E74] text-sm tracking-wide">{k}</span>
                  <span className="text-[#4A403A] text-sm text-right">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Zone d'intervention */}
      <section data-testid="zone-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-28">
        <div className="max-w-2xl mb-14">
          <p className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#8B7E74] mb-4">
            <MapPin size={14} /> Zone d'intervention
          </p>
          <h2 className="font-serif text-4xl md:text-5xl text-[#4A403A] leading-tight">
            Autour de Dijon,<br/><em className="text-[#BA8E82]">à 30–40 min</em>.
          </h2>
          <p className="mt-6 text-[#8B7E74] leading-relaxed text-lg">
            J'interviens à votre domicile dans un rayon d'environ 30 à 40 minutes de route
            autour de Dijon (rue Isabelle de Portugal), de Malain à l'ouest jusqu'aux communes de l'est.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          {/* Carte réelle interactive */}
          <MapZone />

          {/* Détails de la zone */}
          <div className="bg-white rounded-3xl p-8 md:p-10 border border-[#E5DCD6] shadow-softer flex flex-col">
            <div className="flex items-center gap-4 mb-6">
              <span className="inline-flex w-12 h-12 rounded-full bg-[#F4EBE6] items-center justify-center text-[#BA8E82] shrink-0">
                <Car size={20} strokeWidth={1.6} />
              </span>
              <div>
                <h3 className="font-serif text-2xl text-[#4A403A] leading-tight">Une seule zone, large et souple</h3>
                <p className="text-sm text-[#8B7E74]">Jusqu'à ~30–40 min de route autour de Dijon</p>
              </div>
            </div>

            <p className="text-[#8B7E74] leading-relaxed mb-6">
              Dijon et sa métropole (Chenôve, Talant, Fontaine-lès-Dijon, Longvic, Quetigny,
              Chevigny-Saint-Sauveur, Marsannay-la-Côte…), ainsi que les communes environnantes :
              de Malain, Fleurey-sur-Ouche et Ahuy à l'ouest et au nord, jusqu'à Arc-sur-Tille,
              Genlis, Gevrey-Chambertin et Nuits-Saint-Georges.
            </p>

            <div className="mt-auto space-y-3 pt-6 border-t border-[#E5DCD6]">
              <p className="text-xs tracking-[0.2em] uppercase text-[#8B7E74] mb-2">Pourquoi ce secteur&nbsp;?</p>
              {[
                "Un bassin important de familles avec enfants de 0 à 6 ans",
                "Un accompagnement de proximité, directement à domicile",
                "Des temps de trajet maîtrisés pour rester disponible et réactive",
              ].map((line) => (
                <div key={line} className="flex gap-3 text-sm text-[#4A403A]">
                  <CheckCircle2 size={15} className="text-[#D2A69A] shrink-0 mt-0.5" strokeWidth={1.6} />
                  <span>{line}</span>
                </div>
              ))}
              <p className="pt-4 font-serif text-lg italic text-[#BA8E82]">Proximité. Écoute. Bienveillance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pour qui */}
      <section className="bg-[#F4EBE6]/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="text-xs tracking-[0.25em] uppercase text-[#8B7E74] mb-5">Pour qui&nbsp;?</p>
              <h2 className="font-serif text-4xl md:text-5xl text-[#4A403A] leading-tight">
                Les familles avec enfants<br/>
                de <em className="text-[#BA8E82]">0 à 6 ans</em>.
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-5">
              {[
                "Jeunes parents au retour de la maternité, en quête de repères apaisants.",
                "Familles confrontées à des colères, oppositions ou un sommeil difficile.",
                "Parents qui souhaitent enrichir leur lien avec leur enfant à travers le jeu et l'éveil.",
                "Familles recomposées, séparations, naissances multiples — chaque situation est unique.",
                "Couples qui souhaitent retrouver une cohérence éducative à deux.",
              ].map((line, i) => (
                <div key={i} data-testid={`for-whom-${i}`} className="flex gap-4 items-start">
                  <span className="mt-2 w-2 h-2 rounded-full bg-[#D2A69A] shrink-0" />
                  <p className="text-lg text-[#8B7E74] leading-relaxed">{line}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
